'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { AvgRatingType } from '@theme/types';

type ProductRatingsContextValue = {
  ratings: Map<string, AvgRatingType>;
  register: (sku: string) => void;
};

const ProductRatingsContext =
  createContext<ProductRatingsContextValue | null>(null);

export const ProductRatingsProvider = ({
  initialRatings,
  children
}: {
  initialRatings?: AvgRatingType[];
  children: React.ReactNode;
}) => {
  const [fetched, setFetched] = useState<Map<string, AvgRatingType>>(new Map());
  const [pendingCodes, setPendingCodes] = useState<string[]>([]);

  const initialMap = useMemo(() => {
    const map = new Map<string, AvgRatingType>();
    initialRatings?.forEach((item) => {
      if (item?.sku) map.set(item.sku, item);
    });
    return map;
  }, [initialRatings]);

  const register = useCallback((sku: string) => {
    if (!sku) return;

    setPendingCodes((previous) =>
      previous.includes(sku) ? previous : [...previous, sku]
    );
  }, []);

  const missingCodes = useMemo(
    () =>
      pendingCodes.filter(
        (code) => !initialMap.has(code) && !fetched.has(code)
      ),
    [pendingCodes, initialMap, fetched]
  );

  useEffect(() => {
    if (missingCodes.length === 0) return;

    let cancelled = false;
    const codes = [...missingCodes];

    const fetchRatings = async () => {
      try {
        const response = await fetch(
          `/api/rate-and-review/?sku__in=${encodeURIComponent(
            codes.join(',')
          )}`
        );

        if (!response.ok) throw new Error('failed');

        const data: AvgRatingType[] = await response.json();

        if (cancelled) return;

        setFetched((previous) => {
          const next = new Map(previous);
          codes.forEach((code) => {
            if (!next.has(code)) next.set(code, { avg_rating: 0 });
          });

          data?.forEach((item) => {
            if (item?.sku) next.set(item.sku, item);
          });

          return next;
        });
      } catch (error) {
        console.error('Failed to fetch product ratings:', error);
      }
    };

    fetchRatings();

    return () => {
      cancelled = true;
    };
  }, [missingCodes]);

  const value = useMemo(() => {
    const ratings = new Map(initialMap);
    fetched.forEach((item, code) => ratings.set(code, item));

    return { ratings, register };
  }, [initialMap, fetched, register]);

  return (
    <ProductRatingsContext.Provider value={value}>
      {children}
    </ProductRatingsContext.Provider>
  );
};

export const useProductRating = (sku?: string) => {
  const ctx = useContext(ProductRatingsContext);
  const register = ctx?.register;

  useEffect(() => {
    if (!register || !sku) return;
    register(sku);
  }, [register, sku]);

  if (!ctx || !sku) return undefined;

  const rating = ctx.ratings.get(sku);

  return rating?.avg_rating ? rating : undefined;
};
